<template>
  <header class="hdr" :class="hdrClass">
    <router-link class="hdr__logo" :to="{ name: PATHS_NAME.MAIN }">
      <LogotypeCapital :isAdaptive="screenWidth < 576" />
    </router-link>
    <div class="hdr__nav">
      <HeaderNavigation v-if="!isLessThanLG"/>
    </div>
    <div class="hdr__player">
      <CallStickyAudioplayerPanel />
    </div>
    <SearchButton class="hdr__search" v-if="!isLessThanMD" />
    <div class="hdr__notificatons">
      <CallNotifications />
      <NotificationsWidget class="hdr__notificatons-dropdown" />
    </div>
    <div class="hdr__briefcase">
      <CallBriefcaseWidget class="hdr__briefcase-btn" />
      <BriefcaseWidget class="hdr__briefcase-dropdown" />
    </div>
    <div class="hdr__profile">
      <div class="hdr__subscribe" v-if="!isPaid && !isLoadingUser && SHOWED_NEW_SUBSCRIPTION"> 
        <div class="hdr__subscribe-button" @click="openSubscriptionWidget()">
          <SubscriptionButton />
        </div>
        <transition name="Anim__opacity">
          <SubscriptionTelegramPopup v-if="isWidgetTelegramSubscribeOpen" />
        </transition>
      </div>
      <HeaderProfile class="hdr__user-profile" />
      <div class="hdr__pon-wrapper" v-if="withPon">
        <div class="hdr__pon">
          <PonWidget />
        </div>
      </div>
      <div class="hdr__subscribe-box" ref="subscribeBoxRef" v-else-if="!isLoadingUser && isSubscribe">
        <button class="hdr__subscribe-call-widget" @click="toggleSubscribe">
          {{ daysLeft }} <span class="hdr__subscribe-call-widget-suffix">д</span>
        </button>
        <SubscriptionWidget class="hdr__subscribe-widget" v-if="isOpenSubscribe" />
      </div>
    </div>
    <transition name="Anim__dropdown">
      <SubscriptionPopup
        class="hdr__subscribe-popup"
        v-if="isSubscriptionWidgetOpen"
        @close="closeSubscriptionWidget()"
      />
    </transition>
  </header>
</template>

<script setup>
  import { ref, onBeforeUnmount, onMounted, watch, nextTick } from "vue";
  import { useStore } from "vuex";
  import { computed } from "vue";
  import { PATHS_NAME } from "@shared/routers/consts/pathsName";
  import { useScreenSize } from '@/Groups/Audioplayer/private/imports/screenSize';
  import { SHOWED_NEW_SUBSCRIPTION } from "@shared/constants/globalValues/global";
  //----------------------------
  const { isLessThanLG, isLessThanMD, screenWidth } = useScreenSize();
  //----------------------------
  const store = useStore();
  //----------------------------
  import LogotypeCapital from '@shared/UI/Logotypes/LogotypeCapital/LogotypeCapital.vue';
  import HeaderNavigation from './components/HeaderNavigation/HeaderNavigation.vue';
  import HeaderProfile from './components/Profile/HeaderProfile.vue';
  import SearchButton from './components/Search/SearchButton.vue';
  import SubscriptionPopup from "./components/Subscription/SubscriptionPopup/SubscriptionPopup.vue";
  import SubscriptionButton from "./components/Subscription/SubscriptionButton/SubscriptionButton.vue";
  import CallStickyAudioplayerPanel from '@/Groups/Audioplayer/public/modules/CallStickyAudioplayerPanel/CallStickyAudioplayerPanel.vue';
  import CallNotifications from '@/Groups/Notifications/public/modules/CallNotifications/CallNotifications.vue';
  import NotificationsWidget from '@/Groups/Notifications/public/modules/NotificationsWidget/NotificationsWidget.vue';
  import CallBriefcaseWidget from '@/Groups/Briefcase/public/modules/CallBriefcaseWidget/CallBriefcaseWidget.vue';
  import BriefcaseWidget from "@/Groups/Briefcase/public/modules/BriefcaseWidget/BriefcaseWidget.vue";
  import PonWidget from "@/Groups/Pon/public/PonWidget/PonWidget.vue";
  import SubscriptionWidget from "./components/Subscription/SubscriptionWidget/SubscriptionWidget.vue";
  import SubscriptionTelegramPopup from "./components/Subscription/SubscriptionTelegramPopup/SubscriptionTelegramPopup.vue";
  import CallPonPopup from "@/Groups/Pon/public/CallPonPopup/CallPonPopup.vue";
  //----------------------------
  const isOpenSubscribe = ref(false);
  const subscribeBoxRef = ref(null);
  //----------------------------
  const isWidgetTelegramSubscribeOpen = computed(() => {
    return store.state.UserSystem.isTelegramSubscribeWidgetLast;
  });
  const withPon = computed(() => store.state.UserSystem.user.has_pon_subscription);
  const withTelegramSubscription = computed(() => store.state.UserSystem.user.has_telegram_subscription);
  const get_isModerator = computed(() => store.getters['UserSystem/hasModeratorPrivileges']);
  const isLoadingUser = computed(() => store.getters['UserSystem/hasIsLoading']);
  const isPaid = computed(() => store.getters['UserSystem/hasPaidAccess']);
  const isAuth = computed(() => store.getters['UserSystem/isAuthenticated']);
  /* const showSubscriptionPopup = ref(false); */
  const isSubscribeShow = computed(() => !withPonButton && !withTelegramSubscription);
  const hdrClass = computed(() => {
    if(!isAuth.value){ return 'hdr--without-auth'; }
    else if(!isPaid.value) { return 'hdr--without-paid'; }
    else if(withPon.value) { return 'hdr--with-pon'}
    else return 'hdr-paid';
  })
  const userData = computed(() => store.state.UserSystem.user || {});
  const hasTelegram = computed(() => {
    const telegram = userData.value.telegram;
    return Boolean(telegram?.trim());
  });

  const isSubscribe = computed(() => store.state.UserSystem.user.has_cryptocloud_subscription || 
                                     store.state.UserSystem.user.has_telegram_subscription);

  const daysLeft = computed(() => {
    return store.state.UserSystem.user.cryptocloud_days_left || store.state.UserSystem.user.telegram_days_left || 0;
  });
  //----------------------------
  // Используем Vuex для управления виджетом подписки
  const isSubscriptionWidgetOpen = computed(() => 
    store.getters['HeaderSystem/isSubscriptionWidgetOpen']
  );

  const openSubscriptionWidget = () => {
    store.dispatch('HeaderSystem/openSubscriptionWidget');
  };

  const closeSubscriptionWidget = () => {
    store.dispatch('HeaderSystem/closeSubscriptionWidget');
  };
  //----------------------------
  const handleTelegramWidget = async () => {
    if (isAuth.value && sessionStorage.getItem('telegramWidgetLastOpened') === 'true') {
      await nextTick();
      showSubscriptionPopup.value = true;
      store.commit('UserSystem/SET_TELEGRAM_SUBSCRIBE_WIDGET_LAST', true);
      sessionStorage.setItem('telegramWidgetLastOpened', 'false');
    }
  };

  const toggleSubscribe = () => {
    isOpenSubscribe.value = !isOpenSubscribe.value;
  };

  const handleClickOutside = (event) => {
    if (subscribeBoxRef.value && !subscribeBoxRef.value.contains(event.target)) {
      isOpenSubscribe.value = false;
    }
  };

  onMounted(async () => {
    document.addEventListener("click", handleClickOutside);
    if (isAuth.value) {
      handleTelegramWidget();
    }
  });

  onBeforeUnmount(() => {
    document.removeEventListener("click", handleClickOutside);
  });
  
  watch(isAuth, async (newValue, oldValue) => {
    if (newValue) {
      handleTelegramWidget();
    }
  });
</script>

<style lang="scss" scoped>
  @import "./styles.scss";
</style>